import { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
    IconX, IconCheck, IconZoomIn, IconZoomOut, IconRefresh,
    IconBrush, IconHandStop, IconArrowBackUp, IconArrowForwardUp,
    IconEye, IconSparkles, IconPencil, IconFocusCentered, IconScan,
    IconPalette, IconWand, IconSun, IconContrast, IconDroplet,
    IconUpload, IconPhoto,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { notifications } from '@/lib/notifications';
import { VISA_GROUPS } from './VisaCountryModal';
import {
    BG_GRADIENTS, BG_PATTERNS, BG_PRESETS,
    paintGradient, paintPattern, drawImageCover,
    presetSrc, parseBgToken,
} from './bgPresets';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const MAX_HISTORY = 30;

const BG_HEX = {
    white:     '#ffffff',
    blue:      '#2196F3',
    sky_blue:  '#03A9F4',
    grey:      '#9E9E9E',
    dark_blue: '#3068E1',
    teal:      '#00897B',
    off_white: '#FFF8DC',
};

function computeBgColor(bgId, customColor) {
    if (bgId === 'custom') return customColor || '#ffffff';
    if (bgId === 'transparent') return 'transparent';
    return BG_HEX[bgId] || '#ffffff';
}

// ── Print paper sizes shown inside the modal (configs live in PHOTO_SIZES_CONFIG) ──
const PRINT_SIZES = [
    { base: 'print_2r',     label: '2R' },
    { base: 'print_3r',     label: '3R' },
    { base: 'print_4r',     label: '4R' },
    { base: 'print_5r',     label: '5R' },
    { base: 'print_6r',     label: '6R' },
    { base: 'print_8r',     label: '8R' },
    { base: 'print_a4',     label: 'A4' },
    { base: 'print_letter', label: 'Letter' },
];

// Small canvas thumbnail for pattern / gallery swatches
function SwatchCanvas({ paint, size = 32, radius = 7 }) {
    const ref = useRef(null);
    useEffect(() => {
        const c = ref.current;
        if (!c) return;
        c.width = size; c.height = size;
        paint(c.getContext('2d'), size, size);
    }, [paint, size]);
    return <canvas ref={ref} style={{ width: size, height: size, borderRadius: radius, display: 'block' }} />;
}

export default function EraserModal({
    open, imageUrl, exportW = 600, exportH = 600,
    bgColor: bgColorProp = '#ffffff',
    onClose, onApply,
    // Preview-match props
    initialZoom, initialOffset,
    // Main editor frame dimensions (used to convert zoom/offset coordinate systems)
    mainFrameW = 0, mainFrameH = 0,
    // Bg / size props
    backgrounds = [], initialBg = '', customBgColor = '#ffffff', initialBgImageSrc = '',
    photoSizes = [], sizeConfigs = {}, initialSize = '',
    startAtSavedPosition = false,
    // Color & light props
    initialBrightness = 100, initialContrast = 100, initialSaturation = 100, initialSharp = 0,
    isBgRemoved = false,
}) {
    const { t, currentLanguage } = useTranslation();
    const isEn = (currentLanguage || '').startsWith('en');
    const eLogoLight = isEn ? '/images/logo-en.svg' : '/images/logo.svg';
    const eLogoDark = isEn ? '/images/logo-white-en.svg' : '/images/logo-white.svg';

    const viewRef = useRef(null);
    const offRef  = useRef(null);
    const imgRef  = useRef(null);
    const colorPickerRef = useRef(null);

    const [tool, setTool]           = useState('pan');
    const [brushSize, setBrushSize] = useState(28);
    const [brushHardness, setBrushHardness] = useState(0); // 0=very soft, 100=hard
    const [cursorPos, setCursorPos] = useState(null); // for DOM brush circle overlay
    const [isRefining, setIsRefining] = useState(false); // edge refine animation
    const [zoom, setZoom]           = useState(1);
    const [offset, setOffset]       = useState({ x: 0, y: 0 });
    const [dirty, setDirty]         = useState(false);
    const [showOriginal, setShowOriginal] = useState(false);

    // Local bg / size state
    const [localBg, setLocalBg]               = useState(initialBg || '');
    const [localCustomColor, setLocalCustomColor] = useState(customBgColor);
    const [localSize, setLocalSize]           = useState(initialSize || '');
    const [visaExpanded, setVisaExpanded]     = useState(false);

    // Extended background: kind = 'color' | 'gradient' | 'pattern' | 'gallery' | 'image'
    const [bgKind, setBgKind]         = useState('color');
    const [gradientId, setGradientId] = useState('');
    const [patternId, setPatternId]   = useState('');
    const [galleryId, setGalleryId]   = useState('');
    const [bgImageUrl, setBgImageUrl] = useState('');   // image preview url (preset or upload)
    const [bgImageId, setBgImageId]   = useState('');   // preset id, or 'upload' for a user file
    const bgImageElRef                = useRef(null);    // HTMLImageElement for the image bg
    const [bgImageVersion, setBgImageVersion] = useState(0); // bump when image loads
    const bgFileInputRef              = useRef(null);
    // Print-size sub-panel
    const [printOrientation, setPrintOrientation] = useState('portrait');
    const [printExpanded, setPrintExpanded]       = useState(false);

    const [edgeRefined, setEdgeRefined] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const [localBgRemoved, setLocalBgRemoved]     = useState(isBgRemoved);
    const [isRemovingBgModal, setIsRemovingBgModal] = useState(false);

    // Local color & light adjustments
    const [localBrightness, setLocalBrightness] = useState(initialBrightness);
    const [localContrast, setLocalContrast]     = useState(initialContrast);
    const [localSaturation, setLocalSaturation] = useState(initialSaturation);
    const [localSharp, setLocalSharp]           = useState(initialSharp);

    const historyRef    = useRef([]);
    const historyIdxRef = useRef(-1);
    const [historyLen, setHistoryLen] = useState(0);
    const [historyIdx, setHistoryIdx] = useState(-1);
    const [imageVersion, setImageVersion] = useState(0);

    const drag          = useRef(null);
    const strokeStarted = useRef(false);
    const lassoRef      = useRef([]);
    const prevToolRef   = useRef(null);
    // Converted preview zoom/offset (main editor coords → eraser modal coords)
    const previewZoomRef   = useRef(null);
    const previewOffsetRef = useRef(null);
    // Fit scales stored at open time (used for reverse conversion on apply)
    const fitZoomAtOpenRef      = useRef(0); // eraser modal fit zoom (fz)
    const containScaleAtOpenRef = useRef(0); // main editor contain scale
    const [lassoPts, setLassoPts] = useState([]);
    const canvasDimsRef     = useRef({ w: 800, h: 600 });
    const [isCompact, setIsCompact] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1100);
    const [isMobile, setIsMobile]   = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    const [eraserTab, setEraserTab] = useState(null); // 'tools' | 'bg' | 'adjust' | 'size' | null
    useEffect(() => {
        const handler = () => { setIsCompact(window.innerWidth < 1100); setIsMobile(window.innerWidth < 768); };
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    const frameRectRef      = useRef({ frX: 0, frY: 0, frW: 800, frH: 600 });
    const canvasContainerRef = useRef(null);
    const bgProgressLabelRef = useRef(null); // imperatively updated to avoid re-renders during progress

    // Refs so saveSnapshot always reads the latest values without stale-closure issues
    const localBgRef          = useRef(localBg);
    const localCustomColorRef = useRef(localCustomColor);
    const localSizeRef        = useRef(localSize);
    const bgKindRef           = useRef(bgKind);
    const gradientIdRef       = useRef(gradientId);
    const patternIdRef        = useRef(patternId);
    const galleryIdRef        = useRef(galleryId);
    const bgImageIdRef        = useRef(bgImageId);
    useEffect(() => { bgImageIdRef.current = bgImageId; }, [bgImageId]);
    useEffect(() => { localBgRef.current = localBg; }, [localBg]);
    useEffect(() => { localCustomColorRef.current = localCustomColor; }, [localCustomColor]);
    useEffect(() => { localSizeRef.current = localSize; }, [localSize]);
    useEffect(() => { bgKindRef.current = bgKind; }, [bgKind]);
    useEffect(() => { gradientIdRef.current = gradientId; }, [gradientId]);
    useEffect(() => { patternIdRef.current = patternId; }, [patternId]);
    useEffect(() => { galleryIdRef.current = galleryId; }, [galleryId]);

    const activeBgColor = localBg
        ? computeBgColor(localBg, localCustomColor)
        : bgColorProp;

    const isSpecialBg = bgKind !== 'color';

    // Paint the active background (color / gradient / pattern / image) into a rect.
    // Used by the on-screen preview (redraw).
    const paintActiveBg = useCallback((ctx, x, y, w, h) => {
        if (bgKind === 'gradient') {
            const g = BG_GRADIENTS.find(it => it.id === gradientId);
            if (g) { paintGradient(ctx, x, y, w, h, g); return; }
        } else if (bgKind === 'pattern') {
            const p = BG_PATTERNS.find(it => it.id === patternId);
            if (p) { paintPattern(ctx, x, y, w, h, p); return; }
        } else if (bgKind === 'image' && bgImageElRef.current) {
            ctx.fillStyle = '#ffffff'; ctx.fillRect(x, y, w, h); // backing fill for transparency
            drawImageCover(ctx, bgImageElRef.current, x, y, w, h);
            return;
        }
        ctx.fillStyle = activeBgColor && activeBgColor !== 'transparent' ? activeBgColor : '#ffffff';
        ctx.fillRect(x, y, w, h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bgKind, gradientId, patternId, bgImageVersion, activeBgColor]);

    // ── Snapshots — unified: pixel state + bg/size meta ───────────────────────
    const saveSnapshot = useCallback(() => {
        const off = offRef.current;
        if (!off) return;
        const snap = off.getContext('2d').getImageData(0, 0, off.width, off.height);
        const entry = {
            snap,
            bg:          localBgRef.current,
            customColor: localCustomColorRef.current,
            size:        localSizeRef.current,
            bgKind:      bgKindRef.current,
            gradientId:  gradientIdRef.current,
            patternId:   patternIdRef.current,
            galleryId:   galleryIdRef.current,
            bgImageEl:   bgImageElRef.current,
            bgImageId:   bgImageIdRef.current,
        };
        const newH = historyRef.current.slice(0, historyIdxRef.current + 1);
        if (newH.length >= MAX_HISTORY) newH.shift();
        newH.push(entry);
        historyRef.current = newH;
        historyIdxRef.current = newH.length - 1;
        setHistoryLen(newH.length);
        setHistoryIdx(newH.length - 1);
    }, []);

    const restoreSnapshot = useCallback((idx) => {
        const off = offRef.current;
        if (!off || idx < 0 || idx >= historyRef.current.length) return;
        const entry = historyRef.current[idx];
        off.getContext('2d').putImageData(entry.snap, 0, 0);
        setLocalBg(entry.bg);
        setLocalCustomColor(entry.customColor);
        setLocalSize(entry.size);
        setBgKind(entry.bgKind || 'color');
        setGradientId(entry.gradientId || '');
        setPatternId(entry.patternId || '');
        setGalleryId(entry.galleryId || '');
        bgImageElRef.current = entry.bgImageEl || null;
        setBgImageUrl(entry.bgImageEl?.src || '');
        setBgImageId(entry.bgImageId || '');
        historyIdxRef.current = idx;
        setHistoryIdx(idx);
        setImageVersion(v => v + 1); // force canvas redraw
    }, []);

    // ── Redraw ────────────────────────────────────────────────────────────────
    const redraw = useCallback(() => {
        const vc  = viewRef.current;
        const off = offRef.current;
        if (!vc || !off) return;
        const ctx = vc.getContext('2d');
        ctx.clearRect(0, 0, vc.width, vc.height);

        // Canvas background — clearly different from sidebar in both modes
        const isDark = document.documentElement.classList.contains('dark');
        ctx.fillStyle = isDark ? '#18181b' : '#9ca3af';
        ctx.fillRect(0, 0, vc.width, vc.height);

        // Compute frame rect from selected size (or image dims if no size config)
        const activeSize = localSize || initialSize;
        const config = sizeConfigs[activeSize];
        const fW = config ? config.exportW : off.width;
        const fH = config ? config.exportH : off.height;
        const scale = Math.min(vc.width * 0.85 / fW, vc.height * 0.85 / fH);
        const frW = Math.round(fW * scale);
        const frH = Math.round(fH * scale);
        const frX = Math.round((vc.width - frW) / 2);
        const frY = Math.round((vc.height - frH) / 2);
        frameRectRef.current = { frX, frY, frW, frH };

        // Drop shadow around frame
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.22)';
        ctx.shadowBlur = 18;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = activeBgColor && activeBgColor !== 'transparent' ? activeBgColor : '#ffffff';
        ctx.fillRect(frX, frY, frW, frH);
        ctx.restore();

        // Frame background — special bg (gradient/pattern/gallery/image), solid color, or checkerboard
        if (isSpecialBg) {
            paintActiveBg(ctx, frX, frY, frW, frH);
        } else if (activeBgColor && activeBgColor !== 'transparent') {
            ctx.fillStyle = activeBgColor;
            ctx.fillRect(frX, frY, frW, frH);
        } else {
            const pat = document.createElement('canvas');
            pat.width = 16; pat.height = 16;
            const pctx = pat.getContext('2d');
            pctx.fillStyle = '#aaaaaa'; pctx.fillRect(0, 0, 16, 16);
            pctx.fillStyle = '#dddddd'; pctx.fillRect(0, 0, 8, 8); pctx.fillRect(8, 8, 8, 8);
            ctx.fillStyle = ctx.createPattern(pat, 'repeat');
            ctx.fillRect(frX, frY, frW, frH);
        }

        // Draw image clipped to frame
        const dx = frX + frW / 2 + offset.x - (off.width  * zoom) / 2;
        const dy = frY + frH / 2 + offset.y - (off.height * zoom) / 2;
        const dw = off.width  * zoom;
        const dh = off.height * zoom;

        ctx.save();
        ctx.beginPath();
        ctx.rect(frX, frY, frW, frH);
        ctx.clip();
        ctx.imageSmoothingEnabled = true;
        ctx.filter = showOriginal
            ? 'none'
            : `brightness(${localBrightness/100}) contrast(${localContrast/100}) saturate(${localSaturation/100})`;
        ctx.drawImage(showOriginal && imgRef.current ? imgRef.current : off, dx, dy, dw, dh);
        ctx.filter = 'none';
        ctx.restore();

        // Subtle frame border
        ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.18)';
        ctx.lineWidth = 1;
        ctx.strokeRect(frX, frY, frW, frH);

        // Lasso path overlay
        const pts = lassoRef.current;
        if (tool === 'lasso' && pts.length > 1) {
            ctx.save();
            ctx.strokeStyle = '#fe9a00'; ctx.lineWidth = 2; ctx.setLineDash([6, 3]);
            ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
            pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
            ctx.stroke();
            if (pts.length > 3) {
                ctx.setLineDash([]);
                ctx.strokeStyle = 'rgba(254,154,0,0.4)';
                ctx.beginPath();
                ctx.moveTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
                ctx.lineTo(pts[0].x, pts[0].y);
                ctx.stroke();
            }
            ctx.restore();
        }
    }, [zoom, offset, activeBgColor, isSpecialBg, paintActiveBg, showOriginal, tool, localSize, initialSize, sizeConfigs, localBrightness, localContrast, localSaturation, localSharp]);

    // ── Fit-to-frame zoom helper ──────────────────────────────────────────────
    const fitZoom = useCallback(() => {
        const off = offRef.current;
        if (!off) return 1;
        const { frW, frH } = frameRectRef.current;
        return Math.min(frW / off.width, frH / off.height) * 1.0;
    }, []);

    // ── Load image ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !imageUrl) return;
        setDirty(false);
        setEdgeRefined(false);
        setTool('pan'); setBrushHardness(0); setShowOriginal(false);
        lassoRef.current = []; setLassoPts([]);
        historyRef.current = []; historyIdxRef.current = -1;
        setHistoryLen(0); setHistoryIdx(-1);

        // Reset local bg/size from props each time modal opens
        setLocalBg(initialBg || '');
        setLocalCustomColor(customBgColor);
        setLocalSize(initialSize || '');
        setLocalBgRemoved(isBgRemoved);
        setLocalBrightness(initialBrightness);
        setLocalContrast(initialContrast);
        setLocalSaturation(initialSaturation);
        setLocalSharp(initialSharp);
        setVisaExpanded(false);
        setShowOriginal(false);
        // Restore extended background from the persisted token (gradient/pattern/preset/image)
        const bgDesc = parseBgToken(initialBg);
        setGradientId(''); setPatternId(''); setGalleryId('');
        setBgImageUrl(''); setBgImageId(''); bgImageElRef.current = null;
        if (bgDesc.kind === 'gradient') {
            setBgKind('gradient'); setGradientId(bgDesc.id); setLocalBg('');
        } else if (bgDesc.kind === 'pattern') {
            setBgKind('pattern'); setPatternId(bgDesc.id); setLocalBg('');
        } else if (bgDesc.kind === 'preset') {
            setBgKind('image'); setBgImageId(bgDesc.id); setLocalBg('');
            const src = presetSrc(bgDesc.id); setBgImageUrl(src);
            const im = new Image(); im.onload = () => { bgImageElRef.current = im; setBgImageVersion(v => v + 1); }; im.src = src;
        } else if (bgDesc.kind === 'image' && initialBgImageSrc) {
            setBgKind('image'); setBgImageId('upload'); setLocalBg('');
            setBgImageUrl(initialBgImageSrc);
            const im = new Image(); im.onload = () => { bgImageElRef.current = im; setBgImageVersion(v => v + 1); }; im.src = initialBgImageSrc;
        } else {
            setBgKind('color');
        }
        setPrintExpanded(false); setPrintOrientation('portrait');

        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            imgRef.current = img;
            const off = document.createElement('canvas');
            off.width  = img.naturalWidth;
            off.height = img.naturalHeight;
            off.getContext('2d').drawImage(img, 0, 0);
            offRef.current = off;

            // Poll for canvas mount via RAF — Radix unmounts DialogContent on close,
            // so viewRef.current may be null when onload fires on 2nd open.
            const init = () => {
                const vc = viewRef.current;
                if (!vc) { requestAnimationFrame(init); return; }
                const { w, h } = canvasDimsRef.current;
                vc.width = w; vc.height = h;
                // Compute initial frame rect based on the opening size
                const cfg = sizeConfigs[initialSize];
                const fW = cfg ? cfg.exportW : img.naturalWidth;
                const fH = cfg ? cfg.exportH : img.naturalHeight;
                const scale = Math.min(w * 0.85 / fW, h * 0.85 / fH);
                const frW = Math.round(fW * scale), frH = Math.round(fH * scale);
                const frX = Math.round((w - frW) / 2), frY = Math.round((h - frH) / 2);
                frameRectRef.current = { frX, frY, frW, frH };
                const fz = Math.min(frW / img.naturalWidth, frH / img.naturalHeight);
                fitZoomAtOpenRef.current = fz;
                // If restoring a saved eraser position, use it directly
                if (startAtSavedPosition && initialZoom != null) {
                    containScaleAtOpenRef.current = 0; // no reverse conversion for saved positions
                    previewZoomRef.current   = initialZoom;
                    previewOffsetRef.current = initialOffset || { x: 0, y: 0 };
                    setZoom(initialZoom);
                    setOffset(initialOffset || { x: 0, y: 0 });
                } else if (initialZoom != null && mainFrameW > 0 && mainFrameH > 0) {
                    // Convert main editor zoom/offset to eraser modal coordinate space
                    const containScale = Math.min(mainFrameW / img.naturalWidth, mainFrameH / img.naturalHeight);
                    containScaleAtOpenRef.current = containScale;
                    const pZoom = fz * initialZoom;
                    const pOffX = containScale > 0 ? fz * (initialOffset?.x ?? 0) / containScale : 0;
                    const pOffY = containScale > 0 ? fz * (initialOffset?.y ?? 0) / containScale : 0;
                    previewZoomRef.current   = pZoom;
                    previewOffsetRef.current = { x: pOffX, y: pOffY };
                    setZoom(pZoom);
                    setOffset({ x: pOffX, y: pOffY });
                } else {
                    containScaleAtOpenRef.current = 0;
                    previewZoomRef.current   = fz;
                    previewOffsetRef.current = { x: 0, y: 0 };
                    setZoom(fz);
                    setOffset({ x: 0, y: 0 });
                }
                setImageVersion(v => v + 1);
                // Save initial state as the first history entry
                const initSnap = offRef.current.getContext('2d').getImageData(0, 0, offRef.current.width, offRef.current.height);
                const initEntry = { snap: initSnap, bg: initialBg || '', customColor: customBgColor, size: initialSize || '', bgKind: 'color', gradientId: '', patternId: '', galleryId: '', bgImageEl: null, bgImageId: '' };
                historyRef.current = [initEntry];
                historyIdxRef.current = 0;
                setHistoryLen(1);
                setHistoryIdx(0);
            };
            requestAnimationFrame(init);
        };
        img.src = imageUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, imageUrl]);

    useEffect(() => { redraw(); }, [redraw, zoom, offset, lassoPts, showOriginal, activeBgColor, imageVersion]);

    useEffect(() => {
        if (!open) return;
        const vc = viewRef.current;
        if (!vc) return;
        const { w, h } = canvasDimsRef.current;
        vc.width = w; vc.height = h;
        redraw();
    }, [open]);

    // ── Hide global floating buttons when modal is open ───────────────────────
    useEffect(() => {
        window.dispatchEvent(new CustomEvent(open ? 'eraser-modal-open' : 'eraser-modal-close'));
        return () => { if (open) window.dispatchEvent(new CustomEvent('eraser-modal-close')); };
    }, [open]);

    // ── Reset zoom when size changes ─────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const vc = viewRef.current, off = offRef.current;
        if (!vc || !off) return;
        const activeSize = localSize || initialSize;
        const cfg = sizeConfigs[activeSize];
        const fW = cfg ? cfg.exportW : off.width;
        const fH = cfg ? cfg.exportH : off.height;
        const scale = Math.min(vc.width * 0.85 / fW, vc.height * 0.85 / fH);
        const frW = Math.round(fW * scale), frH = Math.round(fH * scale);
        const frX = Math.round((vc.width - frW) / 2), frY = Math.round((vc.height - frH) / 2);
        frameRectRef.current = { frX, frY, frW, frH };
        const fz = Math.min(frW / off.width, frH / off.height) * 1.0;
        setZoom(fz);
        setOffset({ x: 0, y: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localSize]);

    // ── View mode toggle ──────────────────────────────────────────────────────
    const applyFit = useCallback(() => {
        const z = fitZoom();
        if (z) { setZoom(z); setOffset({ x: 0, y: 0 }); }
    }, [fitZoom]);

    const applyPreview = useCallback(() => {
        if (previewZoomRef.current != null) {
            setZoom(previewZoomRef.current);
            setOffset(previewOffsetRef.current || { x: 0, y: 0 });
        } else applyFit();
    }, [applyFit]);


    // ── Coordinate transform ──────────────────────────────────────────────────
    const viewToImage = useCallback((vx, vy) => {
        const off = offRef.current;
        if (!off) return { x: 0, y: 0 };
        const { frX, frY, frW, frH } = frameRectRef.current;
        const dx = frX + frW / 2 + offset.x - (off.width  * zoom) / 2;
        const dy = frY + frH / 2 + offset.y - (off.height * zoom) / 2;
        return { x: (vx - dx) / zoom, y: (vy - dy) / zoom };
    }, [zoom, offset]);

    // ── Erase stroke ─────────────────────────────────────────────────────────
    const doErase = useCallback((vx, vy) => {
        const off = offRef.current;
        if (!off) return;
        const { x, y } = viewToImage(vx, vy);
        const r = brushSize / zoom;
        const ctx = off.getContext('2d');
        ctx.globalCompositeOperation = 'destination-out';
        const h = brushHardness / 100; // 0=soft, 1=hard
        if (h >= 0.99) {
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        } else {
            const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
            grad.addColorStop(0, 'rgba(0,0,0,1)');
            if (h > 0.01) grad.addColorStop(h, 'rgba(0,0,0,1)'); // solid core up to hardness %
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = grad;
            ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalCompositeOperation = 'source-over';
        setDirty(true);
        redraw();
    }, [viewToImage, brushSize, zoom, brushHardness, redraw]);

    // ── Lasso apply ───────────────────────────────────────────────────────────
    const applyLasso = useCallback(() => {
        const off = offRef.current;
        const pts = lassoRef.current;
        if (!off || pts.length < 3) { lassoRef.current = []; setLassoPts([]); return; }
        saveSnapshot();
        const imgPts = pts.map(p => viewToImage(p.x, p.y));
        const mask = document.createElement('canvas');
        mask.width = off.width; mask.height = off.height;
        const mctx = mask.getContext('2d');
        mctx.fillStyle = 'black'; mctx.fillRect(0, 0, mask.width, mask.height);
        mctx.fillStyle = 'white';
        mctx.beginPath(); mctx.moveTo(imgPts[0].x, imgPts[0].y);
        imgPts.slice(1).forEach(p => mctx.lineTo(p.x, p.y));
        mctx.closePath(); mctx.fill();
        const ctx = off.getContext('2d');
        // destination-out: erases pixels where mask is white (inside lasso)
        ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(mask, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
        lassoRef.current = []; setLassoPts([]);
        setDirty(true);
        redraw();
    }, [saveSnapshot, viewToImage, redraw]);

    // ── Edge refine ───────────────────────────────────────────────────────────
    const refineEdges = useCallback(() => {
        const off = offRef.current;
        if (!off) return;
        saveSnapshot();
        const ctx = off.getContext('2d');
        const w = off.width, h = off.height;
        const src = ctx.getImageData(0, 0, w, h);
        const dst = ctx.createImageData(w, h);
        dst.data.set(src.data);
        const p = src.data;
        for (let y = 1; y < h - 1; y++) {
            for (let x = 1; x < w - 1; x++) {
                const i = (y * w + x) * 4;
                const a = p[i + 3];
                if (a === 0) continue;
                const n = [p[((y-1)*w+x)*4+3], p[((y+1)*w+x)*4+3], p[(y*w+x-1)*4+3], p[(y*w+x+1)*4+3]];
                const avgN = (n[0]+n[1]+n[2]+n[3]) / 4;
                if (avgN < 220) {
                    dst.data[i+3] = Math.min(a, Math.round((a+avgN+avgN)/3));
                    if (a < 220) {
                        let rS=0,gS=0,bS=0,cnt=0;
                        for (let dy=-2;dy<=2;dy++) for (let dx=-2;dx<=2;dx++) {
                            const ny=y+dy,nx=x+dx;
                            if (ny<0||ny>=h||nx<0||nx>=w) continue;
                            const ni=(ny*w+nx)*4;
                            if (p[ni+3]>200) { rS+=p[ni];gS+=p[ni+1];bS+=p[ni+2];cnt++; }
                        }
                        if (cnt>0) { dst.data[i]=Math.round(rS/cnt);dst.data[i+1]=Math.round(gS/cnt);dst.data[i+2]=Math.round(bS/cnt); }
                    }
                }
            }
        }
        ctx.putImageData(dst, 0, 0);
        setDirty(true);
        setEdgeRefined(true);
        redraw();
        // Animate scan effect over the frame (purely visual, not modifying image)
        setIsRefining(true);
        setTimeout(() => setIsRefining(false), 1400);
        notifications.show({ message: t("eraser.edgeRefineDone"), color: 'green' });
    }, [saveSnapshot, redraw]);

    // ── Apply / Reset ─────────────────────────────────────────────────────────
    const handleApply = useCallback(() => {
        const changes = {};
        if (localBg && localBg !== initialBg)           changes.bg = localBg;
        if (localCustomColor !== customBgColor)         changes.customColor = localCustomColor;
        if (localSize && localSize !== initialSize)     changes.size = localSize;

        // Encode the background as a non-destructive token + optional image src.
        // The main editor renders/exports it; nothing is baked into the subject here,
        // so the selection survives save → reload and stays re-editable.
        if (bgKind === 'gradient') {
            changes.bg = `gradient:${gradientId}`; changes.bgImageSrc = '';
        } else if (bgKind === 'pattern') {
            changes.bg = `pattern:${patternId}`; changes.bgImageSrc = '';
        } else if (bgKind === 'image') {
            if (bgImageId === 'upload') { changes.bg = 'image'; changes.bgImageSrc = bgImageUrl; }
            else { changes.bg = `preset:${bgImageId}`; changes.bgImageSrc = presetSrc(bgImageId); }
        } else {
            // Solid color → clear any previously-set image src in the main editor
            changes.bgImageSrc = '';
        }

        // Pass color & light adjustments back to main editor
        changes.brightness = localBrightness;
        changes.contrast   = localContrast;
        changes.saturation = localSaturation;
        changes.sharp      = localSharp;
        // Always pass current zoom/offset so caller can save the view position
        changes.eraserZoom   = zoom;
        changes.eraserOffset = { x: offset.x, y: offset.y };
        // Convert eraser modal zoom/offset back to main editor coordinate space
        const fz = fitZoomAtOpenRef.current;
        const cs = containScaleAtOpenRef.current;
        if (fz > 0 && cs > 0) {
            changes.mainEditorZoom   = zoom / fz;
            changes.mainEditorOffset = { x: offset.x * cs / fz, y: offset.y * cs / fz };
        }
        const off = offRef.current;
        if (!off) return;

        // Always export the transparent subject — the background is composited by the main editor.
        off.toBlob(blob => {
            if (blob) onApply(URL.createObjectURL(blob), changes);
        }, 'image/png');
    }, [onApply, localBg, localCustomColor, localSize, initialBg, customBgColor, initialSize, zoom, offset, localBrightness, localContrast, localSaturation, localSharp, bgKind, gradientId, patternId, bgImageId, bgImageUrl]);

    const resetView = useCallback(() => {
        const z = fitZoom();
        if (z) { setZoom(z); setOffset({ x: 0, y: 0 }); }
    }, [fitZoom]);

    // ── Auto-remove BG when user selects a colour without bg being removed ────
    const handleModalBgRemove = useCallback(async (onDone) => {
        const off = offRef.current;
        if (!off || isRemovingBgModal) return;
        setIsRemovingBgModal(true);
        if (bgProgressLabelRef.current) bgProgressLabelRef.current.textContent = t("eraser.removingBg");
        try {
            // Export current offscreen canvas as PNG blob → URL
            const blob = await new Promise(res => off.toBlob(res, 'image/png'));
            const url  = URL.createObjectURL(blob);

            await new Promise((resolve, reject) => {
                const worker = new Worker(new URL('./bgRemovalWorker.js', import.meta.url), { type: 'module' });
                worker.onmessage = (e) => {
                    const { type, blob: resultBlob, message, pct } = e.data;
                    if (type === 'progress') {
                        if (bgProgressLabelRef.current)
                            bgProgressLabelRef.current.textContent = pct >= 100
                                ? t("eraser.finishing")
                                : `${t("eraser.removingBg")} ${pct}%`;
                    } else if (type === 'done') {
                        worker.terminate();
                        URL.revokeObjectURL(url);
                        // Draw bg-removed result back into offRef canvas
                        const img = new Image();
                        img.onload = () => {
                            off.getContext('2d').clearRect(0, 0, off.width, off.height);
                            off.getContext('2d').drawImage(img, 0, 0, off.width, off.height);
                            URL.revokeObjectURL(img.src);
                            setLocalBgRemoved(true);
                            setDirty(true);
                            // Save history snapshot after removal
                            historyRef.current = historyRef.current.slice(0, historyIdxRef.current + 1);
                            const snap = off.getContext('2d').getImageData(0, 0, off.width, off.height);
                            const entry = { snap, bg: localBgRef.current, customColor: localCustomColorRef.current, size: localSizeRef.current, bgKind: bgKindRef.current, gradientId: gradientIdRef.current, patternId: patternIdRef.current, galleryId: galleryIdRef.current, bgImageEl: bgImageElRef.current, bgImageId: bgImageIdRef.current };
                            historyRef.current.push(entry);
                            historyIdxRef.current = historyRef.current.length - 1;
                            setHistoryLen(historyRef.current.length);
                            setHistoryIdx(historyIdxRef.current);
                            resolve();
                            onDone?.();
                            redrawRef.current?.();
                        };
                        img.onerror = reject;
                        img.src = URL.createObjectURL(resultBlob);
                    } else if (type === 'error') {
                        worker.terminate();
                        URL.revokeObjectURL(url);
                        reject(new Error(message));
                    }
                };
                worker.onerror = (err) => { worker.terminate(); URL.revokeObjectURL(url); reject(err); };
                worker.postMessage({ imageUrl: url });
            });
        } catch (err) {
            console.error('Modal bg removal failed:', err);
            notifications.show({ message: t("eraser.removeBgError"), color: 'red' });
        } finally {
            setIsRemovingBgModal(false);
        }
    }, [isRemovingBgModal]);

    // Choose a non-solid background (gradient / pattern / gallery / uploaded image).
    // Mirrors the color-swatch behaviour: remove the subject's bg first if needed.
    const selectSpecialBg = useCallback((apply) => {
        if (!localBgRemoved) {
            handleModalBgRemove(() => { saveSnapshot(); apply(); });
        } else {
            saveSnapshot(); apply();
        }
    }, [localBgRemoved, handleModalBgRemove, saveSnapshot]);

    const onBgImageFile = useCallback((e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            selectSpecialBg(() => {
                bgImageElRef.current = img;
                setBgImageUrl(url);
                setBgImageId('upload');
                setBgKind('image');
                setBgImageVersion(v => v + 1);
            });
        };
        img.onerror = () => { URL.revokeObjectURL(url); notifications.show({ message: t("eraser.imageLoadError"), color: 'red' }); };
        img.src = url;
    }, [selectSpecialBg]);

    // Select a preset backdrop image from /public/images/bg-presets
    const selectPresetImage = useCallback((preset) => {
        const img = new Image();
        img.onload = () => {
            selectSpecialBg(() => {
                bgImageElRef.current = img;
                setBgImageUrl(preset.src);
                setBgImageId(preset.id);
                setBgKind('image');
                setBgImageVersion(v => v + 1);
            });
        };
        img.onerror = () => notifications.show({ message: t("eraser.imageLoadError"), color: 'red' });
        img.src = preset.src;
    }, [selectSpecialBg]);

    // Pick a solid color swatch — clears any special background kind.
    const chooseColor = useCallback((id, isCustom) => {
        const apply = () => {
            setBgKind('color');
            setGradientId(''); setPatternId(''); setGalleryId('');
            bgImageElRef.current = null; setBgImageUrl(''); setBgImageId('');
            setLocalBg(id);
        };
        if (isCustom) colorPickerRef.current?.click();
        if (!localBgRemoved) handleModalBgRemove(() => { saveSnapshot(); apply(); });
        else { saveSnapshot(); apply(); }
    }, [localBgRemoved, handleModalBgRemove, saveSnapshot]);

    // ── Undo / Redo ──────────────────────────────────────────────────────────
    const undo = useCallback(() => { restoreSnapshot(historyIdxRef.current - 1); redraw(); setEdgeRefined(false); }, [restoreSnapshot, redraw]);
    const redo = useCallback(() => { restoreSnapshot(historyIdxRef.current + 1); redraw(); }, [restoreSnapshot, redraw]);

    // Stable refs so the keyboard handler never needs to re-register when
    // callbacks are recreated (prevents double-fire of refineEdges / saveSnapshot).
    const refineEdgesRef  = useRef(refineEdges);
    const saveSnapshotRef = useRef(saveSnapshot);
    const redrawRef       = useRef(redraw);
    const undoRef         = useRef(undo);
    const redoRef         = useRef(redo);
    const handleApplyRef  = useRef(handleApply);
    const onCloseRef      = useRef(onClose);
    const resetViewRef    = useRef(resetView);
    useEffect(() => { refineEdgesRef.current  = refineEdges;  }, [refineEdges]);
    useEffect(() => { saveSnapshotRef.current = saveSnapshot; }, [saveSnapshot]);
    useEffect(() => { redrawRef.current       = redraw;       }, [redraw]);
    useEffect(() => { undoRef.current         = undo;         }, [undo]);
    useEffect(() => { redoRef.current         = redo;         }, [redo]);
    useEffect(() => { handleApplyRef.current  = handleApply;  }, [handleApply]);
    useEffect(() => { onCloseRef.current      = onClose;      }, [onClose]);
    useEffect(() => { resetViewRef.current    = resetView;    }, [resetView]);

    // ── ResizeObserver: keep canvas pixel dims ≥ natural frame size ─────────────
    useEffect(() => {
        if (!open) return;
        const container = canvasContainerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return;

        // Minimum canvas pixel dims — at least as wide as the CSS min-width of the container
        const minW = Math.max(Math.ceil(exportW / 0.85), 860);
        const minH = Math.ceil(exportH / 0.85);

        const observer = new ResizeObserver(() => {
            const vc = viewRef.current;
            if (!vc) return;
            const newW = Math.max(minW, container.clientWidth);
            const newH = Math.max(minH, container.clientHeight);
            if (vc.width !== newW || vc.height !== newH) {
                vc.width  = newW;
                vc.height = newH;
                canvasDimsRef.current = { w: newW, h: newH };
                redrawRef.current?.();
            }
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [open, exportW, exportH]);

    // ── Keyboard shortcuts ────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            const tag = e.target?.tagName;
            if (tag === 'INPUT' && e.target.type !== 'range') return;
            if (tag === 'TEXTAREA') return;
            const ctrl = e.ctrlKey || e.metaKey;
            const alt  = e.altKey;
            if (ctrl && e.key === 'z') { e.preventDefault(); e.shiftKey ? redoRef.current() : undoRef.current(); return; }
            if (ctrl && e.key === 'y') { e.preventDefault(); redoRef.current(); return; }
            if (ctrl && (e.key==='+' || e.key==='=')) { e.preventDefault(); setZoom(z=>clamp(z+0.15,0.1,8)); return; }
            if (ctrl && e.key === '-') { e.preventDefault(); setZoom(z=>clamp(z-0.15,0.1,8)); return; }
            if (ctrl && e.key === '0') { e.preventDefault(); resetViewRef.current(); return; }
            if (ctrl && e.key === '1') { e.preventDefault(); setZoom(1); return; }
            // Ctrl + Backspace → clear canvas (erase all pixels)
            if (ctrl && e.key === 'Backspace') {
                e.preventDefault();
                const off = offRef.current;
                if (off) {
                    saveSnapshotRef.current();
                    const ctx = off.getContext('2d');
                    ctx.globalCompositeOperation = 'destination-out';
                    ctx.fillStyle = 'rgba(0,0,0,1)';
                    ctx.fillRect(0, 0, off.width, off.height);
                    ctx.globalCompositeOperation = 'source-over';
                    setDirty(true);
                    redrawRef.current();
                }
                return;
            }
            // Alt + Ctrl + R → Edge Refine
            if (ctrl && alt && (e.key === 'r' || e.key === 'R')) { e.preventDefault(); refineEdgesRef.current(); return; }
            if (!ctrl) {
                if (e.key==='h'||e.key==='H') { e.preventDefault(); setTool('pan'); return; }
                if (e.key==='b'||e.key==='B') { e.preventDefault(); setTool('erase'); return; }
                if (e.key==='[') { e.preventDefault(); setBrushSize(s=>clamp(s-4,4,120)); return; }
                if (e.key===']') { e.preventDefault(); setBrushSize(s=>clamp(s+4,4,120)); return; }
                // D → Default colors (reset bg to white)
                if (e.key==='d'||e.key==='D') { e.preventDefault(); saveSnapshotRef.current(); setBgKind('color'); setGradientId(''); setPatternId(''); setGalleryId(''); bgImageElRef.current=null; setBgImageUrl(''); setBgImageId(''); setLocalBg('white'); return; }
                if (e.key===' ' && !e.repeat) { e.preventDefault(); setTool(cur=>{prevToolRef.current=cur;return 'pan';}); return; }
                if (e.key==='Enter') { e.preventDefault(); handleApplyRef.current(); return; }
                if (e.key==='Escape') { e.preventDefault(); onCloseRef.current(); return; }
            }
        };
        const onKeyUp = (e) => {
            if (e.key===' ' && prevToolRef.current!==null) { setTool(prevToolRef.current); prevToolRef.current=null; }
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); };
    // Only re-register when the modal open state changes — all callbacks accessed via stable refs
    }, [open]);

    // ── Pointer events ────────────────────────────────────────────────────────
    const getPos = (e) => {
        const vc = viewRef.current;
        const r  = vc.getBoundingClientRect();
        return { x: (e.clientX-r.left)*(vc.width/r.width), y: (e.clientY-r.top)*(vc.height/r.height) };
    };

    const onPointerDown = (e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        const { x, y } = getPos(e);
        if (tool === 'erase') {
            if (!strokeStarted.current) { saveSnapshot(); strokeStarted.current = true; }
            doErase(x, y);
        } else if (tool === 'lasso') {
            lassoRef.current = [{ x, y }]; setLassoPts([{ x, y }]);
        } else {
            drag.current = { startX: x, startY: y, startOx: offset.x, startOy: offset.y };
            setIsDragging(true);
        }
    };
    const onPointerMove = (e) => {
        const { x, y } = getPos(e);
        // Track canvas-relative position for brush cursor overlay
        if (tool === 'erase') {
            const vc = viewRef.current;
            if (vc) {
                const rect = vc.getBoundingClientRect();
                setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }
        }
        if (tool==='erase' && e.buttons===1) doErase(x, y);
        else if (tool==='lasso' && e.buttons===1) { lassoRef.current.push({x,y}); setLassoPts([...lassoRef.current]); }
        else if (tool==='pan' && drag.current && e.buttons===1) setOffset({ x: drag.current.startOx+(x-drag.current.startX), y: drag.current.startOy+(y-drag.current.startY) });
    };
    const onPointerUp = () => { drag.current=null; strokeStarted.current=false; setIsDragging(false); if(tool==='lasso') applyLasso(); };

    const onWheel = useCallback((e) => {
        e.preventDefault();
        setZoom(z=>clamp(z+(e.deltaY<0?0.12:-0.12),0.1,8));
    }, []);

    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => {
            const vc = viewRef.current;
            if (!vc) return;
            vc.addEventListener('wheel', onWheel, { passive: false });
        }, 50);
        return () => { clearTimeout(timer); viewRef.current?.removeEventListener('wheel', onWheel); };
    }, [open, onWheel]);

    const cursor = tool==='erase' ? 'none'
        : tool==='lasso' ? 'crosshair'
        : isDragging ? 'grabbing' : 'grab';

    const SIDEBAR_W       = isMobile ? 0 : 220;
    const RIGHT_SIDEBAR_W = isCompact ? 0 : 220;
    const HEADER_H  = 52;
    const MOBILE_BOTTOM_NAV_H = isMobile ? 56 : 0;
    const TOOLBAR_H = 40; // eraser toolbar (zoom controls row) height
    const vw = typeof window !== 'undefined' ? window.innerWidth  : 1400;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
    // Canvas fills exactly the visible area — no forced minimum to avoid horizontal scroll hiding the frame
    const CANVAS_MIN_W = Math.round(vw - SIDEBAR_W - RIGHT_SIDEBAR_W - 2);
    canvasDimsRef.current = { w: CANVAS_MIN_W, h: Math.round(vh - HEADER_H - TOOLBAR_H - MOBILE_BOTTOM_NAV_H - 2) };

    const canUndo = historyIdx > 0;
    const canRedo = historyIdx < historyLen - 1;

    const S = (active=false, disabled=false) => ({
        display:'flex',alignItems:'center',gap:8,width:'100%',padding:'8px 10px',
        borderRadius:8,border:'none',
        background: active ? 'rgba(230,138,0,0.12)' : 'transparent',
        color: disabled ? 'var(--eraser-text-dim)' : active ? '#E68A00' : 'var(--eraser-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 13, fontWeight: active ? 600 : 400,
        transition: 'background 0.12s, color 0.12s',
        outline: 'none', opacity: disabled ? 0.38 : 1,
        boxShadow: active ? 'inset 0 0 0 1.5px rgba(230,138,0,0.3)' : 'none',
    });
    const iconS = (active=false, disabled=false) => ({
        flexShrink: 0,
        color: disabled ? 'var(--eraser-text-dim)' : active ? '#E68A00' : 'var(--eraser-text-muted)',
    });
    const iconBtn = (disabled=false, active=false) => ({
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 7,
        border: active ? '1.5px solid rgba(230,138,0,0.5)' : '1px solid var(--eraser-border)',
        background: active ? 'rgba(230,138,0,0.1)' : 'transparent',
        color: disabled ? 'var(--eraser-text-dim)' : active ? '#E68A00' : 'var(--eraser-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0, opacity: disabled ? 0.38 : 1,
        transition: 'background 0.12s, border-color 0.12s',
    });

    const SectionLabel = ({ children }) => (
        <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--eraser-text-dim)',
            letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '0 2px 5px', margin: 0 }}>
            {children}
        </p>
    );
    const Divider = () => (
        <div style={{ height: 1, background: 'var(--eraser-border)', margin: '8px 0' }} />
    );

    // Shared swatch styling for color / gradient / pattern / gallery tiles
    const swatchStyle = (selected, sz = 32) => ({
        width: sz, height: sz, borderRadius: 7,
        cursor: 'pointer', position: 'relative', flexShrink: 0,
        boxShadow: selected
            ? '0 0 0 2px #E68A00, 0 0 0 3.5px rgba(230,138,0,0.2)'
            : '0 0 0 1.5px var(--eraser-border)',
        transition: 'box-shadow 0.12s',
    });
    const subLabelStyle = { fontSize: 9, fontWeight: 700, color: 'var(--eraser-text-dim)',
        letterSpacing: '0.06em', textTransform: 'uppercase', margin: '9px 2px 4px', padding: 0 };

    const activeLocalSize = localSize || initialSize;

    // Switch portrait/landscape — instantly re-applies the active print size in the new orientation
    const applyPrintOrientation = (val) => {
        setPrintOrientation(val);
        const cur = localSize || initialSize;
        if (cur.startsWith('print_')) {
            const key = cur.replace(/_l$/, '') + (val === 'landscape' ? '_l' : '');
            if (key !== cur) { saveSnapshot(); setLocalSize(key); }
        }
    };

    // Color & light sliders — rendered in right sidebar normally,
    // moved into left sidebar when the window is compact (< 1100 px).
    const colorLightPanel = (
        <div style={{ background: 'var(--eraser-bg)', borderRadius: 10,
            border: '1px solid var(--eraser-border)', padding: '10px 8px', marginTop: 8 }}>
            <SectionLabel>{t("eraser.colorLight")}</SectionLabel>

            {/* Brightness */}
            <div style={{ padding: '4px 4px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--eraser-text-muted)', fontWeight: 500 }}>{t("eraser.brightness")}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: localBrightness !== 100 ? '#E68A00' : 'var(--eraser-text-dim)',
                        background: localBrightness !== 100 ? 'rgba(230,138,0,0.1)' : 'transparent',
                        borderRadius: 4, padding: '1px 6px' }}>
                        {localBrightness > 100 ? `+${localBrightness - 100}` : localBrightness - 100}%
                    </span>
                </div>
                <input type="range" min={0} max={200} step={1} value={localBrightness}
                    onChange={(e) => setLocalBrightness(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#E68A00' }} />
            </div>

            {/* Contrast */}
            <div style={{ padding: '4px 4px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--eraser-text-muted)', fontWeight: 500 }}>{t("eraser.contrast")}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: localContrast !== 100 ? '#E68A00' : 'var(--eraser-text-dim)',
                        background: localContrast !== 100 ? 'rgba(230,138,0,0.1)' : 'transparent',
                        borderRadius: 4, padding: '1px 6px' }}>
                        {localContrast > 100 ? `+${localContrast - 100}` : localContrast - 100}%
                    </span>
                </div>
                <input type="range" min={0} max={200} step={1} value={localContrast}
                    onChange={(e) => setLocalContrast(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#E68A00' }} />
            </div>

            {/* Saturation */}
            <div style={{ padding: '4px 4px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--eraser-text-muted)', fontWeight: 500 }}>{t("eraser.saturation")}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: localSaturation !== 100 ? '#E68A00' : 'var(--eraser-text-dim)',
                        background: localSaturation !== 100 ? 'rgba(230,138,0,0.1)' : 'transparent',
                        borderRadius: 4, padding: '1px 6px' }}>
                        {localSaturation > 100 ? `+${localSaturation - 100}` : localSaturation - 100}%
                    </span>
                </div>
                <input type="range" min={0} max={200} step={1} value={localSaturation}
                    onChange={(e) => setLocalSaturation(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#E68A00' }} />
            </div>

            {/* Sharpness */}
            <div style={{ padding: '4px 4px 4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--eraser-text-muted)', fontWeight: 500 }}>{t("eraser.sharpness")}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: localSharp !== 0 ? '#E68A00' : 'var(--eraser-text-dim)',
                        background: localSharp !== 0 ? 'rgba(230,138,0,0.1)' : 'transparent',
                        borderRadius: 4, padding: '1px 6px' }}>
                        {localSharp * 10}%
                    </span>
                </div>
                <input type="range" min={0} max={10} step={1} value={localSharp}
                    onChange={(e) => setLocalSharp(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#E68A00' }} />
            </div>

            {/* Reset */}
            {(localBrightness !== 100 || localContrast !== 100 || localSaturation !== 100 || localSharp !== 0) && (
                <button type="button"
                    onClick={() => { setLocalBrightness(100); setLocalContrast(100); setLocalSaturation(100); setLocalSharp(0); }}
                    style={{ ...S(false), fontSize: 11, marginTop: 4, justifyContent: 'center',
                        border: '1px dashed var(--eraser-border)' }}>
                    {t("eraser.reset")}
                </button>
            )}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="p-0 overflow-hidden"
                showClose={false}
                overlayClassName="z-[500]"
                style={{
                    width:'100vw',maxWidth:'100vw',height:'100dvh',maxHeight:'100dvh',
                    display:'flex',flexDirection:'column',borderRadius:0,
                    zIndex: 501,
                    '--eraser-bg':'var(--color-body,#ffffff)',
                    '--eraser-sidebar-bg':'var(--color-surface,#f4f4f5)',
                    '--eraser-border':'var(--color-border,#e4e4e7)',
                    '--eraser-text':'var(--color-text,#18181b)',
                    '--eraser-text-muted':'var(--color-text-muted,#71717a)',
                    '--eraser-text-dim':'var(--color-text-subtle,#a1a1aa)',
                    background:'var(--eraser-bg)',border:'none',
                }}
            >
                <DialogTitle className="sr-only">{t("eraser.editImage")}</DialogTitle>

                {/* Full-modal interaction blocker during bg removal */}
                {isRemovingBgModal && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 9999, cursor: 'wait' }} />
                )}

                {/* ══ Header ══ */}
                {(() => {
                    const EPS = 0.001;
                    const positionDirty = previewZoomRef.current != null && (
                        Math.abs(zoom - previewZoomRef.current) > EPS ||
                        Math.abs(offset.x - (previewOffsetRef.current?.x ?? 0)) > EPS ||
                        Math.abs(offset.y - (previewOffsetRef.current?.y ?? 0)) > EPS
                    );
                    // Current background expressed as the persisted token, compared to what we opened with
                    const currentBgToken =
                        bgKind === 'gradient' ? `gradient:${gradientId}`
                        : bgKind === 'pattern' ? `pattern:${patternId}`
                        : bgKind === 'image' ? (bgImageId === 'upload' ? 'image' : `preset:${bgImageId}`)
                        : localBg;
                    const hasChanges = dirty || positionDirty
                        || currentBgToken !== initialBg
                        || (bgKind === 'image' && bgImageId === 'upload')
                        || localCustomColor !== customBgColor
                        || localSize !== initialSize;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10,
                            padding: isMobile ? '0 10px' : '0 14px', height: HEADER_H, flexShrink: 0,
                            borderBottom: '1px solid var(--eraser-border)',
                            background: 'var(--eraser-bg)', overflow: 'hidden' }}>

                            {/* Logo — hidden on mobile to save space */}
                            {!isMobile && (
                                <>
                                    <img src={eLogoLight} alt="Logo" className="dark:hidden"
                                        style={{ height: 24, objectFit: 'contain', flexShrink: 0 }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    <img src={eLogoDark} alt="Logo" className="hidden dark:block"
                                        style={{ height: 24, objectFit: 'contain', flexShrink: 0 }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    {/* Separator */}
                                    <div style={{ width: 1, height: 18, background: 'var(--eraser-border)', flexShrink: 0 }} />
                                </>
                            )}

                            {/* Title */}
                            <span style={{ fontSize: isMobile ? 13 : 13, fontWeight: 700, color: 'var(--eraser-text)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                {isMobile ? t('eraser.titleShort') : t('eraser.title')}
                            </span>

                            <div style={{ flex: 1 }} />

                            {/* Hint text */}
                            {hasChanges && (
                                <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                    {isMobile ? `✓ ${t('eraser.changedShort')}` : t('eraser.changed')}
                                </span>
                            )}

                            {/* Apply — icon-only on mobile */}
                            <button onClick={handleApply}
                                style={{ display: 'flex', alignItems: 'center', gap: 6,
                                    padding: isMobile ? '7px 12px' : '7px 16px', borderRadius: 8, border: 'none',
                                    fontSize: 13, fontWeight: 600, flexShrink: 0,
                                    background: hasChanges ? '#16a34a' : 'rgba(22,163,74,0.15)',
                                    color: hasChanges ? '#fff' : 'rgba(22,163,74,0.45)',
                                    cursor: hasChanges ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.15s', boxShadow: hasChanges ? '0 1px 6px rgba(22,163,74,0.3)' : 'none' }}
                                title={`${t('eraser.apply')} (Enter)`}>
                                <IconCheck size={14} />
                                {isMobile ? t('eraser.applyShort') : t('eraser.apply')}
                            </button>

                            {/* Close */}
                            <button onClick={onClose}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 32, height: 32, borderRadius: 8, border: 'none',
                                    background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                                    cursor: 'pointer', flexShrink: 0, transition: 'background 0.12s' }}
                                title={`${t('common.close')} (Esc)`}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444'; }}>
                                <IconX size={15} />
                            </button>
                        </div>
                    );
                })()}

                {/* ══ Body ══ */}
                {/* Fixed flex row — sidebars stay anchored, only center canvas scrolls if needed */}
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                    {/* ── Left Sidebar — hidden on mobile (shown as bottom sheets) ── */}
                    <div style={{ width: SIDEBAR_W, flexShrink: 0, display: isMobile ? 'none' : 'flex', flexDirection: 'column',
                        borderRight: '1px solid var(--eraser-border)',
                        background: 'var(--eraser-sidebar-bg)' }}>
                    {/* Scrollable content */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px',
                        display: 'flex', flexDirection: 'column', gap: 2 }}>

                        {/* ── Tools group ── */}
                        <div style={{ background: 'var(--eraser-bg)', borderRadius: 10,
                            border: '1px solid var(--eraser-border)', padding: '10px 8px', marginBottom: 8 }}>
                            <SectionLabel>{t("eraser.tools")}</SectionLabel>

                            <button type="button" title="Move Tool (H)" style={S(tool==='pan')} onClick={() => setTool('pan')}>
                                <IconHandStop size={15} style={iconS(tool==='pan')} />
                                <span>{t("eraser.moveTool")}</span>
                                <kbd style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.4,
                                    background: 'rgba(0,0,0,0.07)', borderRadius: 3, padding: '1px 5px' }}>H</kbd>
                            </button>

                            <button type="button" title="Brush Tool (B)" style={S(tool==='erase')} onClick={() => setTool('erase')}>
                                <IconBrush size={15} style={iconS(tool==='erase')} />
                                <span>{t("eraser.brushTool")}</span>
                                <kbd style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.4,
                                    background: 'rgba(0,0,0,0.07)', borderRadius: 3, padding: '1px 5px' }}>B</kbd>
                            </button>

                            {/* Brush options */}
                            {tool === 'erase' && (
                                <div style={{ marginTop: 6, paddingTop: 8,
                                    borderTop: '1px solid var(--eraser-border)',
                                    display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {/* Size */}
                                    <div style={{ padding: '0 4px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: 11, color: 'var(--eraser-text-muted)', fontWeight: 500 }}>{t("eraser.brushSize")}</span>
                                            <span style={{ fontSize: 11, fontWeight: 700, color: '#E68A00',
                                                background: 'rgba(230,138,0,0.1)', borderRadius: 4, padding: '1px 6px' }}>
                                                {brushSize}px
                                            </span>
                                        </div>
                                        <input type="range" min={4} max={120} step={2} value={brushSize}
                                            onChange={(e) => setBrushSize(Number(e.target.value))}
                                            style={{ width: '100%', accentColor: '#E68A00' }} />
                                    </div>
                                    {/* Hardness */}
                                    <div style={{ padding: '0 4px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: 11, color: 'var(--eraser-text-muted)', fontWeight: 500 }}>{t("eraser.hardness")}</span>
                                            <span style={{ fontSize: 11, fontWeight: 700,
                                                color: brushHardness > 0 ? '#E68A00' : 'var(--eraser-text-dim)',
                                                background: brushHardness > 0 ? 'rgba(230,138,0,0.1)' : 'transparent',
                                                borderRadius: 4, padding: '1px 6px' }}>
                                                {brushHardness}%
                                            </span>
                                        </div>
                                        <input type="range" min={0} max={100} step={5} value={brushHardness}
                                            onChange={(e) => setBrushHardness(Number(e.target.value))}
                                            style={{ width: '100%', accentColor: '#E68A00' }} />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── Actions group ── */}
                        <div style={{ background: 'var(--eraser-bg)', borderRadius: 10,
                            border: '1px solid var(--eraser-border)', padding: '10px 8px', marginBottom: 8 }}>
                            <SectionLabel>{t("eraser.action")}</SectionLabel>

                            {/* Edge Refine — with active/applied state */}
                            <button type="button" title="Edge Refine (Alt+Ctrl+R)"
                                onClick={refineEdges}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    width: '100%', padding: '8px 10px', borderRadius: 8,
                                    border: edgeRefined ? '1.5px solid rgba(22,163,74,0.5)' : '1px dashed var(--eraser-border)',
                                    background: edgeRefined ? 'rgba(22,163,74,0.08)' : 'transparent',
                                    color: edgeRefined ? '#16a34a' : 'var(--eraser-text)',
                                    cursor: 'pointer', fontSize: 13,
                                    fontWeight: edgeRefined ? 600 : 400,
                                    transition: 'all 0.2s',
                                    outline: 'none',
                                }}>
                                {edgeRefined
                                    ? <IconCheck size={15} style={{ color: '#16a34a', flexShrink: 0 }} />
                                    : <IconWand size={15} style={{ color: 'var(--eraser-text-muted)', flexShrink: 0 }} />
                                }
                                <span>{t("eraser.edgeRefine")}</span>
                                {/* {edgeRefined && (
                                    <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600,
                                        color: '#16a34a', background: 'rgba(22,163,74,0.12)',
                                        borderRadius: 4, padding: '1px 6px' }}>
                                        {t("eraser.applied")} ✓
                                    </span>
                                )} */}
                            </button>


                        </div>

                        {/* Background */}
                        {backgrounds.length > 0 && (
                            <div style={{ background: 'var(--eraser-bg)', borderRadius: 10,
                                border: '1px solid var(--eraser-border)', padding: '10px 8px', marginBottom: 8 }}>
                                <SectionLabel>{t("eraser.background")}</SectionLabel>

                                {/* Solid colors */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 5, padding: '0 2px 2px' }}>
                                    {backgrounds.map(item => {
                                        const isSelected = bgKind === 'color' && (localBg || initialBg) === item.id;
                                        const isTransparent = item.id === 'transparent';
                                        const isCustom = item.id === 'custom';
                                        return (
                                            <div key={item.id}
                                                title={item.label}
                                                onClick={() => chooseColor(item.id, isCustom)}
                                                style={{ ...swatchStyle(isSelected),
                                                    background: isTransparent
                                                        ? 'repeating-conic-gradient(#aaa 0% 25%,#fff 0% 50%) 0 0/10px 10px'
                                                        : isCustom
                                                            ? (bgKind === 'color' && localBg === 'custom' ? localCustomColor : 'linear-gradient(135deg,#667eea,#764ba2)')
                                                            : item.color,
                                                }}>
                                                {isCustom && (
                                                    <>
                                                        <IconPalette size={16} color="white"
                                                            style={{ position: 'absolute', inset: 0, margin: 'auto',
                                                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />
                                                        <input ref={colorPickerRef} type="color" value={localCustomColor}
                                                            onChange={(e) => { setLocalCustomColor(e.target.value); setLocalBg('custom'); setBgKind('color'); }}
                                                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }} />
                                                    </>
                                                )}
                                                {isSelected && !isCustom && !isTransparent && (
                                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconCheck size={14} color="white" strokeWidth={3}
                                                            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Gradient & pattern — combined */}
                                <p style={subLabelStyle}>{t("eraser.gradientPattern")}</p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 5, padding: '0 2px 2px' }}>
                                    {BG_GRADIENTS.map(g => {
                                        const sel = bgKind === 'gradient' && gradientId === g.id;
                                        return (
                                            <div key={g.id} title={t("eraser.gradient")}
                                                onClick={() => selectSpecialBg(() => { setBgKind('gradient'); setGradientId(g.id); })}
                                                style={{ ...swatchStyle(sel, 26), background: g.css }}>
                                                {sel && <IconCheck size={12} color="#fff" strokeWidth={3} style={{ position: 'absolute', inset: 0, margin: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} />}
                                            </div>
                                        );
                                    })}
                                    {BG_PATTERNS.map(p => {
                                        const sel = bgKind === 'pattern' && patternId === p.id;
                                        return (
                                            <div key={p.id} title={t("eraser.pattern")}
                                                onClick={() => selectSpecialBg(() => { setBgKind('pattern'); setPatternId(p.id); })}
                                                style={{ ...swatchStyle(sel, 26), overflow: 'hidden' }}>
                                                <SwatchCanvas size={26} radius={6} paint={(c, w, h) => paintPattern(c, 0, 0, w, h, p)} />
                                                {sel && <IconCheck size={12} color="#fff" strokeWidth={3} style={{ position: 'absolute', inset: 0, margin: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} />}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Image backgrounds — preset gallery + upload */}
                                <p style={subLabelStyle}>{t("eraser.imageBackground")}</p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 5, padding: '0 2px 2px' }}>
                                    {/* Upload tile */}
                                    <div title={t("eraser.uploadImage")}
                                        onClick={() => bgFileInputRef.current?.click()}
                                        style={{ ...swatchStyle(bgKind === 'image' && bgImageId === 'upload', 30), overflow: 'hidden',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: bgKind === 'image' && bgImageId === 'upload' && bgImageUrl
                                                ? `center/cover no-repeat url(${bgImageUrl})`
                                                : 'var(--eraser-sidebar-bg)' }}>
                                        {!(bgKind === 'image' && bgImageId === 'upload' && bgImageUrl) && <IconUpload size={14} style={{ color: 'var(--eraser-text-muted)' }} />}
                                    </div>
                                    <input ref={bgFileInputRef} type="file" accept="image/*" onChange={onBgImageFile} style={{ display: 'none' }} />
                                    {BG_PRESETS.map(preset => {
                                        const sel = bgKind === 'image' && bgImageId === preset.id;
                                        return (
                                            <div key={preset.id} title={preset.id}
                                                onClick={() => selectPresetImage(preset)}
                                                style={{ ...swatchStyle(sel, 30), overflow: 'hidden',
                                                    background: `center/cover no-repeat url(${preset.src})` }}>
                                                {sel && <IconCheck size={13} color="#fff" strokeWidth={3} style={{ position: 'absolute', inset: 0, margin: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}


                        {/* Color & light — shown inside left sidebar only when compact */}
                        {isCompact && colorLightPanel}

                        {/* Shortcuts — shown inside left sidebar only when compact */}
                        {isCompact && (
                            <div style={{ marginTop: 12, padding: '10px 8px',
                                borderTop: '1px solid var(--eraser-border)' }}>
                                <SectionLabel>{t("eraser.shortcuts")}</SectionLabel>
                                {[
                                    ['B', t("eraser.brushTool")],
                                    ['H', t("eraser.moveTool")],
                                    ['[ / ]', t("eraser.brushSizeShortcut")],
                                    ['Alt+Ctrl+R', t("eraser.edgeRefine")],
                                    ['Ctrl+⌫', t("eraser.clearCanvas")],
                                    ['D', t("eraser.defaultColor")],
                                    ['Ctrl++', t("eraser.zoomInLabel")],
                                    ['Ctrl+-', t("eraser.zoomOutLabel")],
                                    ['Ctrl+Z', t("eraser.undoLabel")],
                                    ['Enter', t("eraser.applyShort")],
                                ].map(([key, label]) => (
                                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between',
                                        alignItems: 'center', padding: '3px 2px',
                                        borderBottom: '1px solid var(--eraser-border)' }}>
                                        <span style={{ fontSize: 10, color: 'var(--eraser-text-muted)' }}>{label}</span>
                                        <kbd style={{ fontSize: 9, opacity: 0.6, background: 'rgba(0,0,0,0.07)',
                                            borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace' }}>{key}</kbd>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>{/* end scrollable */}

                    {/* Photo size — pinned to bottom */}
                    {photoSizes.length > 0 && (
                        <div style={{ flexShrink: 0, borderTop: '1px solid var(--eraser-border)',
                            padding: '10px 10px', background: 'var(--eraser-sidebar-bg)' }}>
                            <SectionLabel>{t("eraser.photoSize")}</SectionLabel>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {photoSizes
                                .filter(item => initialSize === 'dual_image'
                                    ? item.value === 'dual_image'
                                    : item.value !== 'dual_image')
                                .map(item => {
                                    const isVisa = item.value === 'bd_visa';
                                    const isActive = isVisa
                                        ? (activeLocalSize === 'bd_visa' || activeLocalSize.startsWith('visa_'))
                                        : activeLocalSize === item.value;
                                    return (
                                        <div key={item.value}>
                                            <button type="button"
                                                style={{ ...S(isActive), justifyContent: 'flex-start', fontSize: 12 }}
                                                onClick={() => {
                                                    if (isVisa) { setVisaExpanded(v => !v); }
                                                    else { saveSnapshot(); setLocalSize(item.value); setVisaExpanded(false); }
                                                }}>
                                                <item.icon size={14} style={iconS(isActive)} />
                                                <span>{item.abbr}</span>
                                                {isVisa && (
                                                    <span style={{ marginLeft: 'auto', fontSize: 9, opacity: 0.5 }}>
                                                        {visaExpanded ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </button>
                                            {isVisa && visaExpanded && (
                                                <div style={{ paddingLeft: 8, paddingTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {VISA_GROUPS.map(g => (
                                                        <button key={g.key} type="button"
                                                            onClick={() => { saveSnapshot(); setLocalSize(g.key); }}
                                                            style={{ ...S(activeLocalSize === g.key), padding: '4px 8px', fontSize: 11 }}>
                                                            <span>{g.size}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Print paper sizes (2R/3R/4R/5R/6R/8R/A4/Letter) */}
                                {initialSize !== 'dual_image' && (
                                    <div>
                                        <button type="button"
                                            style={{ ...S(activeLocalSize.startsWith('print_')), justifyContent: 'flex-start', fontSize: 12 }}
                                            onClick={() => setPrintExpanded(v => !v)}>
                                            <IconPhoto size={14} style={iconS(activeLocalSize.startsWith('print_'))} />
                                            <span>{t("eraser.printSize")}</span>
                                            <span style={{ marginLeft: 'auto', fontSize: 9, opacity: 0.5 }}>{printExpanded ? '▲' : '▼'}</span>
                                        </button>
                                        {printExpanded && (
                                            <div style={{ paddingLeft: 8, paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    {[['portrait', t("eraser.portrait")], ['landscape', t("eraser.landscape")]].map(([val, lbl]) => (
                                                        <button key={val} type="button"
                                                            onClick={() => applyPrintOrientation(val)}
                                                            style={{ ...S(printOrientation === val), flex: 1, justifyContent: 'center', fontSize: 10, padding: '5px 4px' }}>
                                                            {lbl}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4 }}>
                                                    {PRINT_SIZES.map(ps => {
                                                        const key = ps.base + (printOrientation === 'landscape' ? '_l' : '');
                                                        const active = activeLocalSize === ps.base || activeLocalSize === ps.base + '_l';
                                                        return (
                                                            <button key={ps.base} type="button"
                                                                onClick={() => { saveSnapshot(); setLocalSize(key); setVisaExpanded(false); }}
                                                                style={{ ...S(active), justifyContent: 'center', fontSize: 11, padding: '6px 2px' }}>
                                                                {ps.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    </div>{/* end sidebar */}

                    {/* ── Canvas area ── */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

                        {/* Toolbar — fixed above scroll area, never moves on canvas pan */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 8px',
                            borderBottom: '1px solid var(--eraser-border)',
                            background: 'var(--eraser-bg)', flexShrink: 0, overflow: 'hidden' }}>

                            <button type="button" title={t("eraser.zoomOutTitle")}
                                onClick={() => setZoom(z => clamp(z - 0.15, 0.1, 8))} style={iconBtn()}>
                                <IconZoomOut size={13} />
                            </button>

                            <span onClick={() => setZoom(1)} title="Ctrl+1 — 100%"
                                style={{ fontSize: 12, fontWeight: 700, color: 'var(--eraser-text)',
                                    minWidth: 38, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}>
                                {Math.round(zoom * 100)}%
                            </span>

                            <button type="button" title={t("eraser.zoomInTitle")}
                                onClick={() => setZoom(z => clamp(z + 0.15, 0.1, 8))} style={iconBtn()}>
                                <IconZoomIn size={13} />
                            </button>

                            <button type="button" title={t("eraser.viewReset")}
                                onClick={resetView} style={iconBtn()}>
                                <IconRefresh size={12} />
                            </button>

                            {/* Zoom slider — hidden on mobile to save space */}
                            {!isMobile && (
                                <input
                                    type="range"
                                    min={10} max={400} step={5}
                                    value={Math.round(zoom * 100)}
                                    onChange={(e) => setZoom(Number(e.target.value) / 100)}
                                    title={t("eraser.zoomSlider")}
                                    style={{ width: 80, accentColor: '#E68A00', cursor: 'pointer', flexShrink: 0 }}
                                />
                            )}

                            <div style={{ width: 1, height: 16, background: 'var(--eraser-border)', margin: '0 2px', flexShrink: 0 }} />

                            {/* Before / After toggle */}
                            <button type="button"
                                title={showOriginal ? t("eraser.showEdited") : t("eraser.showOriginal")}
                                onClick={() => setShowOriginal(v => !v)}
                                style={{ ...iconBtn(false, showOriginal), width: 'auto', padding: '0 8px', gap: 4, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                                {showOriginal
                                    ? <><IconScan size={13} />{!isMobile && <span>{t("eraser.afterEdit")}</span>}</>
                                    : <><IconFocusCentered size={13} /><span>{isMobile ? t("eraser.beforeShort") : t("eraser.beforePreview")}</span></>
                                }
                            </button>

                            <div style={{ width: 1, height: 16, background: 'var(--eraser-border)', margin: '0 2px', flexShrink: 0 }} />

                            <button type="button" title={t("eraser.undoTitle")}
                                onClick={undo} disabled={!canUndo} style={iconBtn(!canUndo)}>
                                <IconArrowBackUp size={13} />
                            </button>
                            <button type="button" title={t("eraser.redoTitle")}
                                onClick={redo} disabled={!canRedo} style={iconBtn(!canRedo)}>
                                <IconArrowForwardUp size={13} />
                            </button>

                            {!isMobile && (
                                <>
                                    <div style={{ flex: 1 }} />
                                    <span style={{ fontSize: 11, color: 'var(--eraser-text-dim)', flexShrink: 0 }}>
                                        {tool === 'erase' && t("eraser.brushHelp")}
                                        {tool === 'pan' && t("eraser.panHelp")}
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Scroll wrapper — only the canvas scrolls, toolbar stays fixed above */}
                        <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
                    <div ref={canvasContainerRef} style={{ width: '100%', height: canvasDimsRef.current.h + 'px', minWidth: CANVAS_MIN_W, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>

                        {/* Canvas */}
                        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#9ca3af' }}
                            onPointerLeave={() => setCursorPos(null)}>

                            {/* CSS keyframes for edge refine animation */}
                            <style>{`
                                @keyframes spin {
                                    to { transform: rotate(360deg); }
                                }
                                @keyframes eraserScanLine {
                                    0%   { top: -4px; opacity: 0; }
                                    8%   { opacity: 1; }
                                    92%  { opacity: 1; }
                                    100% { top: 100%; opacity: 0; }
                                }
                                @keyframes eraserGlowPulse {
                                    0%   { opacity: 0; }
                                    25%  { opacity: 1; }
                                    75%  { opacity: 1; }
                                    100% { opacity: 0; }
                                }
                            `}</style>

                            {/* BG removal spinner overlay — covers canvas, matches main editor style */}
                            {isRemovingBgModal && (
                                <div style={{ position: 'absolute', inset: 0, zIndex: 30,
                                    background: 'rgba(0,0,0,0.35)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                                        background: 'rgba(0,0,0,0.72)', color: '#fff',
                                        padding: '8px 18px', borderRadius: 20,
                                        fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        <span style={{ width: 14, height: 14, borderRadius: '50%',
                                            border: '2px solid rgba(255,255,255,0.4)',
                                            borderTopColor: '#fff', display: 'inline-block',
                                            animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                        <span ref={bgProgressLabelRef}>{t("eraser.removingBg")}</span>
                                    </div>
                                </div>
                            )}

                            {/* Edge refine scan animation — purely visual, doesn't touch image pixels */}
                            {isRefining && (() => {
                                const { frX, frY, frW, frH } = frameRectRef.current;
                                return (
                                    <div style={{ position: 'absolute', left: frX, top: frY,
                                        width: frW, height: frH, overflow: 'hidden',
                                        pointerEvents: 'none', zIndex: 20, borderRadius: 1 }}>
                                        {/* Glow tint */}
                                        <div style={{ position: 'absolute', inset: 0,
                                            background: 'rgba(230,138,0,0.07)',
                                            animation: 'eraserGlowPulse 1.4s ease-in-out forwards' }} />
                                        {/* Scan line */}
                                        <div style={{ position: 'absolute', left: 0, width: '100%', height: 4,
                                            background: 'linear-gradient(to bottom, transparent, rgba(230,138,0,0.85), transparent)',
                                            animation: 'eraserScanLine 1.4s ease-in-out forwards' }} />
                                    </div>
                                );
                            })()}

                            {/* Brush cursor circle overlay — no browser size limit */}
                            {tool === 'erase' && cursorPos && (
                                <div style={{
                                    position: 'absolute',
                                    left: cursorPos.x - brushSize,
                                    top:  cursorPos.y - brushSize,
                                    width:  brushSize * 2,
                                    height: brushSize * 2,
                                    borderRadius: '50%',
                                    background: 'rgba(254,154,0,0.14)',
                                    border: '1.5px solid #fe9a00',
                                    pointerEvents: 'none',
                                    zIndex: 15,
                                    boxSizing: 'border-box',
                                }} />
                            )}

                            {/* SVG sharpen filter — applied as CSS filter on the canvas element below */}
                            {localSharp > 0 && (
                                <svg style={{ display: 'none' }}>
                                    <defs>
                                        <filter id="eraser-modal-sharpen">
                                            <feConvolveMatrix order="3"
                                                kernelMatrix={`0 ${-(localSharp/10).toFixed(3)} 0 ${-(localSharp/10).toFixed(3)} ${(1+4*(localSharp/10)).toFixed(3)} ${-(localSharp/10).toFixed(3)} 0 ${-(localSharp/10).toFixed(3)} 0`}
                                                preserveAlpha="true" />
                                        </filter>
                                    </defs>
                                </svg>
                            )}
                            <canvas ref={viewRef}
                                style={{ display: 'block', width: '100%', height: '100%', cursor,
                                    filter: localSharp > 0 ? 'url(#eraser-modal-sharpen)' : 'none' }}
                                onPointerDown={onPointerDown}
                                onPointerMove={onPointerMove}
                                onPointerUp={onPointerUp}
                                onPointerLeave={onPointerUp} />

                            {showOriginal && (
                                <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                                    background: '#E68A00', color: '#fff', fontSize: 12, fontWeight: 700,
                                    padding: '4px 14px', borderRadius: 20, pointerEvents: 'none' }}>
                                    {t("eraser.originalImage")}
                                </div>
                            )}
                        </div>
                    </div>{/* end canvas container */}
                    </div>{/* end scroll wrapper */}
                    </div>{/* end canvas column */}

                    {/* ── Right Sidebar — hidden when compact (< 1100px), sliders move to left sidebar ── */}
                    {!isCompact && (
                        <div style={{ width: RIGHT_SIDEBAR_W, flexShrink: 0, display: 'flex', flexDirection: 'column',
                            borderLeft: '1px solid var(--eraser-border)',
                            background: 'var(--eraser-sidebar-bg)' }}>
                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {colorLightPanel}
                            </div>
                        </div>
                    )}{/* end right sidebar */}
                </div>{/* end body flex row */}

                {/* ── Mobile bottom nav + Sheets ── */}
                {isMobile && (
                    <>
                        <nav className="absolute bottom-0 left-0 right-0 h-14 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700 grid grid-cols-4 items-center z-[600]">
                            {/* Tools */}
                            <button type="button" onClick={() => setEraserTab(eraserTab === 'tools' ? null : 'tools')}
                                className={`flex flex-col items-center gap-0.5 py-1 border-none bg-transparent cursor-pointer transition-colors ${eraserTab === 'tools' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <IconBrush size={22} />
                                <span className="text-[9px] font-bold">{t("eraser.tools")}</span>
                            </button>
                            {/* Background */}
                            <button type="button" onClick={() => setEraserTab(eraserTab === 'bg' ? null : 'bg')}
                                className={`flex flex-col items-center gap-0.5 py-1 border-none bg-transparent cursor-pointer transition-colors ${eraserTab === 'bg' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <IconPalette size={22} />
                                <span className="text-[9px] font-bold">{t("eraser.background")}</span>
                            </button>
                            {/* Adjustments */}
                            <button type="button" onClick={() => setEraserTab(eraserTab === 'adjust' ? null : 'adjust')}
                                className={`flex flex-col items-center gap-0.5 py-1 border-none bg-transparent cursor-pointer transition-colors ${eraserTab === 'adjust' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <svg width="22" height="22" viewBox="0 0 26 26" fill="none"><circle cx="8" cy="7" r="2" stroke="currentColor" strokeWidth="1.6"/><path d="M3 7h3M10 7h13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="18" cy="13" r="2" stroke="currentColor" strokeWidth="1.6"/><path d="M3 13h13M20 13h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="10" cy="19" r="2" stroke="currentColor" strokeWidth="1.6"/><path d="M3 19h5M12 19h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                                <span className="text-[9px] font-bold">{t("eraser.adjust")}</span>
                            </button>
                            {/* Photo Size */}
                            <button type="button" onClick={() => setEraserTab(eraserTab === 'size' ? null : 'size')}
                                className={`flex flex-col items-center gap-0.5 py-1 border-none bg-transparent cursor-pointer transition-colors ${eraserTab === 'size' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <svg width="22" height="22" viewBox="0 0 26 26" fill="none"><rect x="4.5" y="3" width="17" height="21" rx="2.5" stroke="currentColor" strokeWidth="1.8"/><circle cx="13" cy="10.5" r="3" stroke="currentColor" strokeWidth="1.6"/><path d="M5.5 24c0-4 3.4-6.5 7.5-6.5s7.5 2.5 7.5 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                                <span className="text-[9px] font-bold">{t("eraser.photoSize")}</span>
                            </button>
                        </nav>

                        {/* Tools Sheet */}
                        <Sheet open={eraserTab === 'tools'} onOpenChange={(o) => { if (!o) setEraserTab(null); }}>
                            <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[75vh] overflow-hidden flex flex-col bg-white dark:bg-zinc-900" showClose={false}>
                                <SheetTitle className="sr-only">Tools</SheetTitle>
                                                                <div className="flex items-center justify-between px-4 pt-2.5 pb-2">
                                    <span className="text-[15px] font-bold text-zinc-900 dark:text-zinc-100">{t("eraser.toolsActions")}</span>
                                    <button type="button" onClick={() => setEraserTab(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 border-none cursor-pointer"><IconX size={15} /></button>
                                </div>
                                <div className="overflow-y-auto px-3 pb-6 flex flex-col gap-2">
                                    <div className="bg-zinc-50 dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-2">
                                        <button type="button" onClick={() => setTool('pan')}
                                            className={`flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors ${tool==='pan' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400' : 'bg-transparent text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}>
                                            <IconHandStop size={16} className={tool==='pan' ? 'text-orange-500' : 'text-zinc-400'} /><span>{t("eraser.moveTool")}</span>
                                        </button>
                                        <button type="button" onClick={() => setTool('erase')}
                                            className={`flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors ${tool==='erase' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400' : 'bg-transparent text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}>
                                            <IconBrush size={16} className={tool==='erase' ? 'text-orange-500' : 'text-zinc-400'} /><span>{t("eraser.brushTool")}</span>
                                        </button>
                                        {tool === 'erase' && (
                                            <div className="mt-2 pt-2 border-t border-zinc-200 dark:border-zinc-700 flex flex-col gap-3 px-1">
                                                <div>
                                                    <div className="flex justify-between mb-1">
                                                        <span className="text-xs text-zinc-500 font-medium">{t("eraser.brushSize")}</span>
                                                        <span className="text-xs font-bold text-orange-500">{brushSize}px</span>
                                                    </div>
                                                    <input type="range" min={4} max={120} value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} className="w-full accent-orange-500" />
                                                </div>
                                                <div>
                                                    <div className="flex justify-between mb-1">
                                                        <span className="text-xs text-zinc-500 font-medium">{t("eraser.brushHardness")}</span>
                                                        <span className="text-xs font-bold text-orange-500">{brushHardness}%</span>
                                                    </div>
                                                    <input type="range" min={0} max={100} value={brushHardness} onChange={(e) => setBrushHardness(Number(e.target.value))} className="w-full accent-orange-500" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="bg-zinc-50 dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-2">
                                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-1 pb-1.5">{t("eraser.action")}</p>
                                        <button type="button" onClick={refineEdges}
                                            className={`flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm cursor-pointer border-none transition-colors ${edgeRefined ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-semibold' : 'bg-transparent text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
                                            style={{ border: edgeRefined ? '1.5px solid rgba(22,163,74,0.4)' : '1px dashed #d4d4d8' }}>
                                            {edgeRefined ? <IconCheck size={15} className="text-emerald-500 shrink-0" /> : <IconWand size={15} className="text-zinc-400 shrink-0" />}
                                            <span>{t("eraser.edgeRefine")}</span>
                                        </button>
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>

                        {/* Background Sheet */}
                        <Sheet open={eraserTab === 'bg'} onOpenChange={(o) => { if (!o) setEraserTab(null); }}>
                            <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[60vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                                <SheetTitle className="sr-only">Background</SheetTitle>
                                                                <div className="flex items-center justify-between px-4 pt-2.5 pb-2">
                                    <span className="text-[15px] font-bold text-zinc-900 dark:text-zinc-100">{t("eraser.background")}</span>
                                    <button type="button" onClick={() => setEraserTab(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500 border-none cursor-pointer"><IconX size={15} /></button>
                                </div>
                                <div className="overflow-y-auto px-3 pb-6 flex flex-col gap-3">
                                    {/* Solid colors */}
                                    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-3">
                                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-0.5 pb-2">{t("eraser.color")}</p>
                                        <div className="grid grid-cols-5 gap-2">
                                            {backgrounds.map(item => {
                                                const isSelected = bgKind === 'color' && (localBg || initialBg) === item.id;
                                                const isTransparent = item.id === 'transparent';
                                                const isCustom = item.id === 'custom';
                                                return (
                                                    <div key={item.id} onClick={() => chooseColor(item.id, isCustom)} className="relative cursor-pointer rounded-xl" style={{
                                                        aspectRatio: '1',
                                                        boxShadow: isSelected ? '0 0 0 2.5px #E68A00, 0 0 0 4px rgba(230,138,0,0.15)' : '0 0 0 1.5px #e4e4e7',
                                                        background: isTransparent ? 'repeating-conic-gradient(#aaa 0% 25%,#fff 0% 50%) 0 0/10px 10px' : isCustom ? (bgKind === 'color' && localBg === 'custom' ? localCustomColor : 'linear-gradient(135deg,#667eea,#764ba2)') : item.color,
                                                    }}>
                                                        {isCustom && <IconPalette size={18} color="white" style={{ position: 'absolute', inset: 0, margin: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />}
                                                        {isSelected && !isCustom && !isTransparent && <div className="absolute inset-0 flex items-center justify-center"><IconCheck size={16} color="white" strokeWidth={3} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} /></div>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Gradient & pattern — combined */}
                                    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-3">
                                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-0.5 pb-2">{t("eraser.gradientPattern")}</p>
                                        <div className="grid grid-cols-6 gap-2">
                                            {BG_GRADIENTS.map(g => {
                                                const sel = bgKind === 'gradient' && gradientId === g.id;
                                                return (
                                                    <div key={g.id} onClick={() => selectSpecialBg(() => { setBgKind('gradient'); setGradientId(g.id); })} className="relative cursor-pointer rounded-xl" style={{
                                                        aspectRatio: '1', background: g.css,
                                                        boxShadow: sel ? '0 0 0 2.5px #E68A00, 0 0 0 4px rgba(230,138,0,0.15)' : '0 0 0 1.5px #e4e4e7',
                                                    }}>
                                                        {sel && <div className="absolute inset-0 flex items-center justify-center"><IconCheck size={15} color="white" strokeWidth={3} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} /></div>}
                                                    </div>
                                                );
                                            })}
                                            {BG_PATTERNS.map(p => {
                                                const sel = bgKind === 'pattern' && patternId === p.id;
                                                return (
                                                    <div key={p.id} onClick={() => selectSpecialBg(() => { setBgKind('pattern'); setPatternId(p.id); })} className="relative cursor-pointer rounded-xl overflow-hidden" style={{
                                                        aspectRatio: '1',
                                                        boxShadow: sel ? '0 0 0 2.5px #E68A00, 0 0 0 4px rgba(230,138,0,0.15)' : '0 0 0 1.5px #e4e4e7',
                                                    }}>
                                                        <SwatchCanvas size={40} radius={10} paint={(c, w, h) => paintPattern(c, 0, 0, w, h, p)} />
                                                        {sel && <div className="absolute inset-0 flex items-center justify-center"><IconCheck size={15} color="white" strokeWidth={3} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} /></div>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Image backgrounds — presets + upload */}
                                    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-3">
                                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-0.5 pb-2">{t("eraser.imageBackground")}</p>
                                        <div className="grid grid-cols-5 gap-2">
                                            <div onClick={() => bgFileInputRef.current?.click()} className="relative cursor-pointer rounded-xl overflow-hidden flex items-center justify-center bg-zinc-100 dark:bg-zinc-700" style={{
                                                aspectRatio: '1',
                                                boxShadow: bgKind === 'image' && bgImageId === 'upload' ? '0 0 0 2.5px #E68A00, 0 0 0 4px rgba(230,138,0,0.15)' : '0 0 0 1.5px #e4e4e7',
                                                background: bgKind === 'image' && bgImageId === 'upload' && bgImageUrl ? `center/cover no-repeat url(${bgImageUrl})` : undefined,
                                            }}>
                                                {!(bgKind === 'image' && bgImageId === 'upload' && bgImageUrl) && <IconUpload size={18} className="text-zinc-400" />}
                                            </div>
                                            <input ref={bgFileInputRef} type="file" accept="image/*" onChange={onBgImageFile} className="hidden" />
                                            {BG_PRESETS.map(preset => {
                                                const sel = bgKind === 'image' && bgImageId === preset.id;
                                                return (
                                                    <div key={preset.id} onClick={() => selectPresetImage(preset)} className="relative cursor-pointer rounded-xl overflow-hidden" style={{
                                                        aspectRatio: '1',
                                                        background: `center/cover no-repeat url(${preset.src})`,
                                                        boxShadow: sel ? '0 0 0 2.5px #E68A00, 0 0 0 4px rgba(230,138,0,0.15)' : '0 0 0 1.5px #e4e4e7',
                                                    }}>
                                                        {sel && <div className="absolute inset-0 flex items-center justify-center"><IconCheck size={16} color="white" strokeWidth={3} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} /></div>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>

                        {/* Adjustments Sheet */}
                        <Sheet open={eraserTab === 'adjust'} onOpenChange={(o) => { if (!o) setEraserTab(null); }}>
                            <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[75vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                                <SheetTitle className="sr-only">Adjustments</SheetTitle>
                                                                <div className="flex items-center justify-between px-4 pt-2.5 pb-2">
                                    <span className="text-[15px] font-bold text-zinc-900 dark:text-zinc-100">{t("eraser.colorLight")}</span>
                                    <button type="button" onClick={() => setEraserTab(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500 border-none cursor-pointer"><IconX size={15} /></button>
                                </div>
                                <div className="overflow-y-auto px-3 pb-6">
                                    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-4 flex flex-col gap-4">
                                        {[
                                            { label: t("eraser.brightness"), icon: <IconSun size={13} />, value: localBrightness, set: setLocalBrightness, min: 50, max: 200, isSharp: false },
                                            { label: t("eraser.contrast"),  icon: <IconContrast size={13} />, value: localContrast,    set: setLocalContrast,    min: 50, max: 200, isSharp: false },
                                            { label: t("eraser.saturation"), icon: <IconDroplet size={13} />,  value: localSaturation, set: setLocalSaturation, min: 0,  max: 200, isSharp: false },
                                            { label: t("eraser.sharpness"),   icon: <IconSparkles size={13} />, value: localSharp,       set: setLocalSharp,       min: 0,  max: 10,  isSharp: true  },
                                        ].map(({ label, icon, value, set, min, max, isSharp }) => (
                                            <div key={label}>
                                                <div className="flex justify-between items-center mb-1.5">
                                                    <span className="text-xs text-zinc-500 dark:text-zinc-400 font-medium flex items-center gap-1.5">{icon}{label}</span>
                                                    <span className="text-xs font-bold text-orange-500">{isSharp ? `${value*10}%` : `${value>=100?`+${value-100}`:`${value-100}`}%`}</span>
                                                </div>
                                                <input type="range" min={min} max={max} value={value} onChange={(e) => set(Number(e.target.value))} className="w-full accent-orange-500" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>

                        {/* Photo Size Sheet */}
                        <Sheet open={eraserTab === 'size'} onOpenChange={(o) => { if (!o) setEraserTab(null); }}>
                            <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[75vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                                <SheetTitle className="sr-only">Photo Size</SheetTitle>
                                                                <div className="flex items-center justify-between px-4 pt-2.5 pb-2">
                                    <span className="text-[15px] font-bold text-zinc-900 dark:text-zinc-100">{t("eraser.photoSize")}</span>
                                    <button type="button" onClick={() => setEraserTab(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500 border-none cursor-pointer"><IconX size={15} /></button>
                                </div>
                                <div className="overflow-y-auto px-3 pb-6">
                                    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-2">
                                        {photoSizes.filter(item => initialSize === 'dual_image' ? item.value === 'dual_image' : item.value !== 'dual_image').map(item => {
                                            const isVisa = item.value === 'bd_visa';
                                            const activeLocalSize = localSize || initialSize;
                                            const isActive = isVisa ? (activeLocalSize === 'bd_visa' || activeLocalSize.startsWith('visa_')) : activeLocalSize === item.value;
                                            return (
                                                <div key={item.value}>
                                                    <button type="button"
                                                        className={`flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm border-none cursor-pointer transition-colors ${isActive ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold' : 'bg-transparent text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}
                                                        onClick={() => { if (isVisa) { setVisaExpanded(v => !v); } else { saveSnapshot(); setLocalSize(item.value); setVisaExpanded(false); setEraserTab(null); } }}>
                                                        <item.icon size={16} className={isActive ? 'text-orange-500 shrink-0' : 'text-zinc-400 shrink-0'} />
                                                        <span>{item.abbr}</span>
                                                        {isVisa && <span className="ml-auto text-[10px] opacity-50">{visaExpanded ? '▲' : '▼'}</span>}
                                                    </button>
                                                    {isVisa && visaExpanded && (
                                                        <div className="pl-3 flex flex-col gap-0.5 mb-1">
                                                            {VISA_GROUPS.map(group => {
                                                                const isGroupActive = activeLocalSize === group.key;
                                                                return (
                                                                    <button key={group.key} type="button"
                                                                        className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs border-none cursor-pointer transition-colors ${isGroupActive ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold' : 'bg-transparent text-zinc-600 dark:text-zinc-400 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}
                                                                        onClick={() => { saveSnapshot(); setLocalSize(group.key); setVisaExpanded(false); setEraserTab(null); }}>
                                                                        <span>{group.sizeBn || group.size}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* Print paper sizes */}
                                        {initialSize !== 'dual_image' && (
                                            <div>
                                                <button type="button"
                                                    className={`flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm border-none cursor-pointer transition-colors ${(localSize || initialSize).startsWith('print_') ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold' : 'bg-transparent text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}
                                                    onClick={() => setPrintExpanded(v => !v)}>
                                                    <IconPhoto size={16} className={(localSize || initialSize).startsWith('print_') ? 'text-orange-500 shrink-0' : 'text-zinc-400 shrink-0'} />
                                                    <span>{t("eraser.printSize")}</span>
                                                    <span className="ml-auto text-[10px] opacity-50">{printExpanded ? '▲' : '▼'}</span>
                                                </button>
                                                {printExpanded && (
                                                    <div className="pl-3 pt-1.5 flex flex-col gap-2 mb-1">
                                                        <div className="flex gap-1.5">
                                                            {[['portrait', t("eraser.portrait")], ['landscape', t("eraser.landscape")]].map(([val, lbl]) => (
                                                                <button key={val} type="button" onClick={() => applyPrintOrientation(val)}
                                                                    className={`flex-1 px-2 py-1.5 rounded-lg text-xs border-none cursor-pointer transition-colors ${printOrientation === val ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold' : 'bg-zinc-50 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 font-medium'}`}>
                                                                    {lbl}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        <div className="grid grid-cols-4 gap-1.5">
                                                            {PRINT_SIZES.map(ps => {
                                                                const key = ps.base + (printOrientation === 'landscape' ? '_l' : '');
                                                                const active = (localSize || initialSize) === ps.base || (localSize || initialSize) === ps.base + '_l';
                                                                return (
                                                                    <button key={ps.base} type="button"
                                                                        onClick={() => { saveSnapshot(); setLocalSize(key); setVisaExpanded(false); setEraserTab(null); }}
                                                                        className={`px-1 py-2 rounded-lg text-xs border-none cursor-pointer transition-colors ${active ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold' : 'bg-zinc-50 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 font-medium'}`}>
                                                                        {ps.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </>
                )}

                {/* ── Shortcuts — fixed bottom-right (hidden in compact mode) ── */}
                {!isCompact && <div style={{
                    position: 'fixed', bottom: 16, right: 16, zIndex: 1100,
                    background: 'var(--eraser-bg)', borderRadius: 10,
                    border: '1px solid var(--eraser-border)', padding: '10px 8px',
                    minWidth: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                }}>
                    <SectionLabel>{t("eraser.shortcuts")}</SectionLabel>
                    {[
                        ['B', t("eraser.brushTool")],
                        ['H', t("eraser.moveTool")],
                        ['[ / ]', t("eraser.brushSizeShortcut")],
                        ['Alt+Ctrl+R', t("eraser.edgeRefine")],
                        ['Ctrl+⌫', t("eraser.clearCanvas")],
                        ['D', t("eraser.defaultColor")],
                        ['Ctrl++', t("eraser.zoomInLabel")],
                        ['Ctrl+-', t("eraser.zoomOutLabel")],
                        ['Ctrl+Z', t("eraser.undoLabel")],
                        ['Enter', t("eraser.applyShort")],
                    ].map(([key, label]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '3px 2px', borderBottom: '1px solid var(--eraser-border)' }}>
                            <span style={{ fontSize: 10, color: 'var(--eraser-text-muted)' }}>{label}</span>
                            <kbd style={{ fontSize: 9, opacity: 0.6, background: 'rgba(0,0,0,0.07)',
                                borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace' }}>{key}</kbd>
                        </div>
                    ))}
                </div>}
            </DialogContent>
        </Dialog>
    );
}
